import { call, put, select, takeLatest } from 'redux-saga/effects';
import _ from 'lodash';
import axios from '../axiosConfig';
import {
  flightSearch,
  flightSuccess,
  flightFailure,
  updateFlightSearchObj,
  pollingFlightSearchRequest,
  initFlightSearchRequest,
} from '../../slices/flight';
import {
  searchFail,
  searchSuccess,
  setFlightTripSearchInputs,
  setFlightTripSearchParam,
} from '../../slices/tripSearch';
import uniqKeyGenerator from '../../../utils/uniqKeyGenerator';
import {
  SearchStatus,
  PortOfDepartureOption,
  SearchModes,
  TravelTypes,
  FlightFilterType,
  ListPageSize,
} from '../../../constants';
import { addToTrip, changeTrip } from '../../slices/trip';
import flightSearchInput from '../../../helpers/selectors/flightSearchInput';
import formatFlightSegmentRequest from '../../../helpers/formatFlightSegmentRequest';
import isFlightSegmentsValid from '../../../helpers/validationHelpers/isFlightSegmentsValid';
import formatFlightFilters from '../../../helpers/formatFlightFilters';
import generateUniqueId from '../../../utils/generateUniqueId';
import formatAirlinesAutocompleteResults from '../../../helpers/formatters/formatAirlinesAutocompleteResults';
import getFiltersForSlider from '../../../helpers/formatters/getFiltersForSlider';
import { checkDoEnableSelectFlightsBySegment } from '../../slices/user';

const queryString = require('query-string');

function* flightSearchWorker({ payload }) {
  let responseData = null;
  try {
    const { travelAgency } = yield select((store) => store.user);
    const doUseUSDForFlightRate =
      travelAgency && travelAgency.settings && travelAgency.settings.useUSDForFlightRate;
    const doEnableSelectFlightsBySegment = yield select(checkDoEnableSelectFlightsBySegment);
    const { currencyCode } = yield select((store) => store.tripSearch);
    const flightRateCodes = yield select((store) => store.user.flightRateCodes);
    const { ignoreCachedResultsForModes } = yield select((store) => store.trip);
    const { searchInputs, hasUserInteractedWithFlightList } = yield select(
      flightSearchInput(payload.tripId, payload.isTripSearch),
    );

    let rateSelections =
      (payload && payload.searchObj && payload.searchObj.rateCodes) ||
      (searchInputs && searchInputs.rateCodes);
    if (flightRateCodes && flightRateCodes.length && !rateSelections) {
      rateSelections = _.map(
        _.filter(flightRateCodes, (r) => r.isSelectedByDefault),
        (r) => r.selectionKey || r.rateCode,
      );
      if (rateSelections.length === 0) rateSelections = undefined;
    }

    const searchStatus =
      (!payload.doAddSelectedFlightToTrip && !payload.doIgnoreStoredResults) ||
      payload.doShowProgressbar
        ? {
            searchStatus: SearchStatus.SEARCHING,
          }
        : {};
    if (payload.isTripSearch) {
      yield put(
        setFlightTripSearchParam({
          ...payload,
          rateCodes: rateSelections,
          ...searchStatus,
        }),
      );
    } else {
      yield put(
        updateFlightSearchObj({
          ...payload,
          rateCodes: rateSelections,
          ...searchStatus,
        }),
      );
    }

    const { portOfDepartureOption } = yield select(
      (store) =>
        store.tripSearch.selectedSearchMode !== SearchModes.HOTEL &&
        store.tripSearch.searchObj.cruise.filters,
    );

    const { staterooms } = yield select((store) => store.tripSearch.searchObj);

    const { searchObj, defaultSearchObj, selectedFlightSegments, breadCrumbsIndex } = yield select(
      flightSearchInput(payload.tripId, payload.isTripSearch),
    );
    // Improve flight segment selection when switching modes
    const flightSearchObj = yield select(
      (store) => store.flight.flightSearches[payload.tripId]?.searchObj,
    );
    if (
      !payload.isTripSearch &&
      !isFlightSegmentsValid(searchObj.flightSegments, searchObj.flightSearchType)
    ) {
      yield put(updateFlightSearchObj({ ...payload, searchStatus: SearchStatus.DONE }));
      return;
    }
    const targetQuery = { ...searchObj };
    if (defaultSearchObj && searchObj) {
      if (defaultSearchObj.filterTimeLayoverMin === searchObj.filterTimeLayoverStart) {
        targetQuery.filterTimeLayoverStart = undefined;
      }
      if (defaultSearchObj.filterTimeLayoverMax === searchObj.filterTimeLayoverEnd) {
        targetQuery.filterTimeLayoverEnd = undefined;
      }
    }

    delete targetQuery.airlinesAutocompleteResults;
    delete targetQuery.flightItinerary;
    delete targetQuery.defaultSearchObj;
    delete targetQuery.flightSegments;
    delete targetQuery.filterTakeoff;
    delete targetQuery.filterLanding;
    delete targetQuery.hasDifferentOutboundInboundCabins;
    delete targetQuery.hasDifferentOutboundInboundAirLine;
    delete targetQuery.hasDifferentOutboundInboundConnectingAirports;
    delete targetQuery.hasDifferentOutboundInboundAlliance;
    delete targetQuery.filterTimeLayoverMax;
    delete targetQuery.filterTimeLayoverMin;
    delete targetQuery.selectedFlightSegments;
    delete targetQuery.breadCrumbsIndex;
    delete targetQuery.staterooms;
    delete targetQuery.isNewSearch;
    delete targetQuery.hasUserInteractedWithFlightList;

    const doIgnoreCachedResults =
      ignoreCachedResultsForModes &&
      ignoreCachedResultsForModes.length &&
      ignoreCachedResultsForModes.indexOf(TravelTypes.flight) > -1
        ? true
        : undefined;
    const alliances =
      searchInputs &&
      !searchInputs.hasDifferentOutboundInboundAlliance &&
      searchObj.flightSegments[0] &&
      searchObj.flightSegments[0].alliances &&
      searchObj.flightSegments[0] &&
      searchObj.flightSegments[0].alliances.length
        ? searchObj.flightSegments[0].alliances.map((item) => item.value)
        : undefined;

    const useFlightStateSegments =
      !Object.keys(searchObj.flightSegments).length ||
      searchObj.flightSegments.every((fs) => !Object.keys(fs).length);

    const segments = useFlightStateSegments
      ? flightSearchObj.flightSegments
      : searchObj.flightSegments;

    const request = {
      ...targetQuery,
      ...formatFlightFilters(
        searchObj.filterTakeoff,
        defaultSearchObj.takeoff,
        FlightFilterType.filterTakeOff,
      ),
      ...formatFlightFilters(
        searchObj.filterLanding,
        defaultSearchObj.landing,
        FlightFilterType.filterLanding,
      ),
      ...formatFlightSegmentRequest(
        segments,
        searchObj.flightSearchType,
        searchInputs,
        doEnableSelectFlightsBySegment ? selectedFlightSegments : undefined,
      ),
      airlines:
        searchInputs && !searchInputs.hasDifferentOutboundInboundAirLine
          ? searchObj.flightSegments[0] &&
            searchObj.flightSegments[0].airlines &&
            searchObj.flightSegments[0].airlines.map((item) => item.value)
          : undefined,
      airlinesPreference:
        searchInputs && !searchInputs.hasDifferentOutboundInboundAirLine
          ? searchObj.flightSegments[0].airlinesPreference
          : undefined,
      alliances,
      alliancePreference:
        alliances &&
        alliances.length &&
        searchInputs &&
        !searchInputs.hasDifferentOutboundInboundAlliance
          ? searchObj.flightSegments[0].alliancePreference
          : undefined,
      filterAirlines:
        searchObj.filterAirlines && searchObj.filterAirlines.length
          ? searchObj.filterAirlines.map((item) => item.value)
          : undefined,
      adults: _.sumBy(staterooms, (st) => st.adults || 0),
      seniors: _.sumBy(staterooms, (st) => st.seniors || 0),
      childrenAge: _.flatMap(staterooms, (st) => Object.values(st.children || [])),
      lapInfants: _.sumBy(staterooms, (st) => st.lapInfants || 0),
      seatInfants: _.sumBy(staterooms, (st) => st.seatInfants || 0),
      doPrioritizeNonstopFlights: portOfDepartureOption === PortOfDepartureOption.NONSTOP,
      doReturnTravelOptions: payload.isTripSearch,
      doIgnoreCachedResults,
      currencyCode: !doUseUSDForFlightRate && currencyCode ? currencyCode : undefined,
      doMergeCached: !hasUserInteractedWithFlightList,
      doGetRemainingFlightsOnly: payload && payload.doHaveMoreFlights,
      tripId: payload && payload.doHaveMoreFlights && payload.tripId,
      requestId: payload && payload.requestId,
      isNdcEnabled:
        window &&
        window.location &&
        window.location.search &&
        window.location.search.indexOf('enableNDC') > -1
          ? true
          : undefined,
      selectedSegmentIndex: doEnableSelectFlightsBySegment ? (breadCrumbsIndex ?? 0) : undefined,
    };
    const qs = queryString.stringify(request, { skipNull: true });
    const responses = yield select((store) => store.flight.responses);
    responseData = responses[qs];

    if (doIgnoreCachedResults)
      yield put(
        changeTrip({
          field: 'ignoreCachedResultsForModes',
          value: _.reject(ignoreCachedResultsForModes, (ss) => ss === TravelTypes.flight),
        }),
      );

    const url = `/flights/search?${qs}`;
    const config = {
      method: 'get',
      url,
    };
    const { data } = yield call(axios, config);
    responseData = data;
    const { searchInputs: searchInputsLatest, requestId: requestIdLatest } = yield select(
      flightSearchInput(payload.tripId, payload.isTripSearch),
    );

    if (
      data &&
      data.doHaveMoreFlights &&
      !(payload || {}).doIgnoreStoredResults &&
      searchInputsLatest.pageSize === ListPageSize
    ) {
      // Call flight search to get more flights only
      yield put(
        pollingFlightSearchRequest({
          tripId: payload.tripId,
          isTripSearch: payload.isTripSearch,
          doHaveMoreFlights: true,
          searchProgress: 10,
          requestId: generateUniqueId(),
          previousRequestId: requestIdLatest,
        }),
      );
    }
    if (payload && payload.doHaveMoreFlights) {
      try {
        const { requestId: currentRequestId } = yield select(
          flightSearchInput(payload.tripId, payload.isTripSearch),
        );

        // Handled case: When new polling started, and previous polling/search stil in progress,
        // it hide progressbar for sometime
        if (payload && payload.requestId === currentRequestId) {
          // Hide flight search progressbar
          if (payload.isTripSearch) {
            yield put(
              setFlightTripSearchParam({
                searchProgress: undefined,
              }),
            );
          } else {
            yield put(
              updateFlightSearchObj({
                searchProgress: null,
                tripId: payload.tripId,
              }),
            );
          }
        }
      } catch (e) {
        console.log(e);
      }
      return;
    }

    // TODO put in request unique ID for response date
    if (Array.isArray(data)) {
      uniqKeyGenerator(data.flightItineraries);
    }

    if (responseData) {
      const { filters } = responseData;
      if (payload.isTripSearch) {
        yield put(
          setFlightTripSearchInputs({
            airlinesAutocompleteResults: formatAirlinesAutocompleteResults(filters),
            ...getFiltersForSlider(filters, 'filterTakeoff', 'filterLanding'),
            defaultSearchObj: {
              ...defaultSearchObj,
              ...getFiltersForSlider(filters),
            },
          }),
        );
        yield put(
          setFlightTripSearchParam({
            ...getFiltersForSlider(filters, 'filterTakeoff', 'filterLanding'),
          }),
        );
        yield put(searchSuccess(responseData));
      } else {
        yield put(
          flightSuccess({
            queryString: qs,
            response: responseData,
            tripId: payload.tripId,
            airlinesAutocompleteResults: formatAirlinesAutocompleteResults(filters),
            searchObj: getFiltersForSlider(filters, 'filterTakeoff', 'filterLanding'),
            defaultSearchObj: getFiltersForSlider(filters),
          }),
        );
      }
    } else if (payload.isTripSearch) {
      yield put(
        setFlightTripSearchInputs({
          airlinesAutocompleteResults: [],
        }),
      );
      yield put(searchFail());
    } else {
      yield put(
        flightSuccess({
          queryString: qs,
          response: responseData,
          tripId: payload.tripId,
          airlinesAutocompleteResults: [],
        }),
      );
    }
  } catch (error) {
    if (payload.isTripSearch) {
      yield put(searchFail(error));
      return;
    }
    yield put(updateFlightSearchObj({ ...payload, searchStatus: SearchStatus.DONE }));
    yield put(flightFailure(error));
  }

  if (payload.doAddSelectedFlightToTrip) {
    let flightItinerary =
      responseData &&
      responseData.flightItineraries &&
      responseData.flightItineraries.length &&
      responseData.flightItineraries[0].flightKey === payload.flightItinerary.flightKey
        ? responseData.flightItineraries[0]
        : payload.flightItinerary;
    if (payload.isTripSearch) {
      flightItinerary =
        responseData &&
        responseData.travelOptions &&
        responseData.travelOptions.length &&
        responseData.travelOptions[0].key === payload.flightItinerary.flightKey
          ? responseData.travelOptions[0].flightItinerary
          : payload.flightItinerary;
    }
    yield put(
      addToTrip({
        tripId: payload.tripId,
        trip: {
          flightItinerary,
        },
      }),
    );
  }
}

function* flightSearchWatcher() {
  yield takeLatest(flightSearch.toString(), flightSearchWorker);
  yield takeLatest(pollingFlightSearchRequest.toString(), flightSearchWorker);
  yield takeLatest(initFlightSearchRequest.toString(), flightSearchWorker);
}

export default flightSearchWatcher;
